import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { readSidebarCollapsed, writeSidebarCollapsed } from './sidebarPreference';
import { Box, Typography, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { usePermissions } from '../contexts/PermissionContext';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
  featureFlag?: string;
  subtitle?: string;
}

interface AppSidebarProps {
  appTitle: string;
  navItems: NavItem[];
  activePath: string;
  accentColor?: string;
  width?: number;
  eyebrow?: string;
  version?: string;
  enabledFeatures?: string[];
}

const FOREST = '#0F1A10';
const ACCENT = '#2C3E2D';
const INK = '#0C0C0B';
const MUTED = '#74706B';
const FAINT = '#B8B3AD';
const RULE = '#E4DFD9';
const SERIF = '"Playfair Display", Georgia, serif';
const SANS = '"DM Sans", system-ui, sans-serif';
const COLLAPSED_WIDTH = 60;

export function AppSidebar({
  appTitle,
  navItems,
  activePath,
  accentColor = '#2C3E2D',
  width = 192,
  eyebrow,
  version = 'v1.0.0',
  enabledFeatures,
}: AppSidebarProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [collapsed, setCollapsed] = useState(() => readSidebarCollapsed() ?? isMobile);
  const { permissions } = usePermissions();

  const currentWidth = collapsed ? COLLAPSED_WIDTH : width;

  const visibleItems = navItems.filter(item => {
    if (item.permission && !permissions.includes(item.permission)) return false;
    if (item.featureFlag && enabledFeatures && !enabledFeatures.includes(item.featureFlag))
      return false;
    return true;
  });

  return (
    <Box
      component="nav"
      sx={{
        width: currentWidth,
        flexShrink: 0,
        bgcolor: '#FFFFFF',
        borderRight: `1px solid ${RULE}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        position: 'fixed',
        top: 64,
        bottom: 0,
        left: 0,
        zIndex: 1000,
      }}
    >
      {/* Header */}
      <Box
        sx={{ px: collapsed ? 1 : '14px', pt: collapsed ? 2 : '16px', pb: collapsed ? 1 : '10px' }}
      >
        {collapsed ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: FOREST,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#FAFAF7',
                  lineHeight: 1,
                }}
              >
                R
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: FAINT,
                mb: '3px',
              }}
            >
              {eyebrow || appTitle}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: INK,
                lineHeight: 1.15,
              }}
            >
              Navigation
            </Typography>
            <Box sx={{ width: 18, height: 2, bgcolor: accentColor, mt: '7px' }} />
          </>
        )}
      </Box>

      {/* Navigation Grid */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: collapsed ? 0.5 : '10px', py: '8px' }}>
        {collapsed ? (
          // Collapsed: vertical icon list
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
            {visibleItems.map(item => {
              const active =
                item.path === '/'
                  ? activePath === '/'
                  : activePath === item.path || activePath.startsWith(item.path + '/');
              return (
                <Tooltip key={item.path} title={item.label} placement="right" arrow>
                  <Box
                    onClick={() => navigate(item.path)}
                    sx={{
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: active ? accentColor : MUTED,
                      bgcolor: active ? 'rgba(44,62,45,0.09)' : 'transparent',
                      border: active ? '1px solid rgba(44,62,45,0.14)' : `1px solid ${RULE}`,
                      '&:hover': { color: INK, bgcolor: 'rgba(44,62,45,0.05)' },
                    }}
                  >
                    {item.icon}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        ) : (
          // Expanded: single column grid
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px' }}>
            {visibleItems.map((item, idx) => {
              const active =
                item.path === '/'
                  ? activePath === '/'
                  : activePath === item.path || activePath.startsWith(item.path + '/');
              const IconEl = item.icon;
              return (
                <Box
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '10px',
                    p: '10px 12px',
                    cursor: 'pointer',
                    color: active ? accentColor : MUTED,
                    bgcolor: active ? 'rgba(44,62,45,0.09)' : 'transparent',
                    border: active ? `1px solid rgba(44,62,45,0.14)` : '1px solid transparent',
                    transition: 'all 0.1s',
                    '&:hover': { color: INK, bgcolor: 'rgba(44,62,45,0.05)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      '& svg': { fontSize: 24 },
                    }}
                  >
                    {IconEl}
                  </Box>
                  <Typography sx={{ color: RULE, fontSize: '0.78rem', mx: 0.5, flexShrink: 0 }}>
                    |
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: active ? 600 : 500,
                        letterSpacing: '0.03em',
                        lineHeight: 1.2,
                      }}
                    >
                      {item.label}
                    </Typography>
                    {item.subtitle && (
                      <Typography
                        sx={{
                          fontSize: '0.58rem',
                          color: FAINT,
                          fontStyle: 'italic',
                          letterSpacing: '0.04em',
                          mt: '2px',
                        }}
                      >
                        {item.subtitle}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: `1px solid ${RULE}`,
          px: collapsed ? 0 : '14px',
          py: collapsed ? 0 : '10px',
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'space-between',
          flexDirection: collapsed ? 'column' : 'row',
        }}
      >
        {!collapsed && (
          <Typography
            sx={{
              fontSize: '0.65rem',
              letterSpacing: '0.1em',
              color: FAINT,
              textTransform: 'uppercase',
            }}
          >
            {version}
          </Typography>
        )}
        <IconButton
          onClick={() =>
            setCollapsed(current => {
              const next = !current;
              writeSidebarCollapsed(next);
              return next;
            })
          }
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          size="small"
          sx={{
            color: MUTED,
            borderRadius: 0,
            mt: collapsed ? 0.5 : 0,
            '&:hover': { color: INK, bgcolor: 'transparent' },
          }}
        >
          {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
}

export default AppSidebar;
